import { useEffect, useState } from 'react';
import { api } from '../api';
import { openAddToDesktop } from '../components/InstallPrompt';

/**
 * ERP 客户端下载页（免登录，路由 /download）
 * - 手机（安卓 / 苹果）：优先引导"添加到主屏幕"，无需下载任何安装包
 * - 电脑：Windows 桌面版 .exe（服务器 downloads/finance-erp.exe）+ 浏览器"安装到桌面"
 */
export default function Download() {
  const [info, setInfo] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);

  const isMobile =
    typeof navigator !== 'undefined' &&
    (/android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 && /macintosh/i.test(navigator.userAgent)));

  useEffect(() => {
    api('/desktop')
      .then((d) => setInfo(d))
      .catch(() => setInfo(null))
      .finally(() => setLoaded(true));
  }, []);

  const sizeMb = info?.available ? (info.size / 1024 / 1024).toFixed(1) : '';
  const updated = info?.updated ? new Date(info.updated).toLocaleDateString('zh-CN') : '';

  // ===== 手机端安装卡片（放到最前，手机上最重要） =====
  const MobileCard = (
    <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-3xl">📱</span>
        <div>
          <h2 className="font-bold text-slate-800">
            手机版 · 添加到主屏幕{' '}
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full align-middle">推荐</span>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">不下载任何安装包，只在本机加个图标，点开全屏用</p>
        </div>
      </div>
      <button
        onClick={() => openAddToDesktop()}
        className="block w-full text-center bg-brand-600 text-white font-bold rounded-xl py-3 mb-3 hover:bg-brand-700 active:scale-95 transition"
      >
        📲 一键添加到手机桌面
      </button>
      <div className="text-sm text-slate-600 space-y-2">
        <p>🟢 <b>安卓</b>：浏览器右上角「⋮」→「安装应用」或「添加到主屏幕」</p>
        <p>🟢 <b>苹果</b>：用 Safari 打开 → 底部「分享」→「添加到主屏幕」</p>
        <p>🟢 <b>微信里</b>：搜「荣牧科技 ERP」小程序，或点右上角「···」→「在浏览器打开」再按上面操作</p>
      </div>
    </div>
  );

  // ===== 电脑端安装卡片 =====
  const DesktopCard = (
    <>
      {/* Windows 桌面版 */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-3xl">💻</span>
          <div>
            <h2 className="font-bold text-slate-800">
              Windows 桌面版（电脑专用）{' '}
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full align-middle">推荐</span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">独立窗口运行，像本地软件一样，不受浏览器限制</p>
          </div>
        </div>

        {isMobile && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 mb-3">
            ⚠️ 您现在用的是手机，<b>这个安装包手机装了也打不开</b>（安卓/苹果不能运行 Windows 程序）。
            手机请用上方「📱 手机版」的方式，或改用电脑下载。
          </div>
        )}

        {!loaded ? (
          <div className="text-sm text-slate-400 py-4 text-center">正在检查服务器上的安装包…</div>
        ) : info?.available ? (
          <div className="space-y-3">
            <a
              href={info.url}
              download
              className="block w-full text-center bg-brand-600 text-white font-bold rounded-xl py-3 hover:bg-brand-700 active:scale-95 transition"
            >
              ⬇ 下载桌面版安装包（约 {sizeMb} MB）
            </a>
            <p className="text-xs text-slate-400 text-center">版本日期：{updated} · 下载后双击运行，无需安装</p>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <p className="font-medium">服务器上暂未上传桌面版安装包</p>
            <p className="text-xs mt-1 text-amber-700">
              请联系荣牧科技管理员，将 <code className="bg-amber-100 px-1 rounded">finance-erp.exe</code> 上传到服务器{' '}
              <code className="bg-amber-100 px-1 rounded">downloads/</code> 文件夹后，这里即可直接下载。
            </p>
            <p className="text-xs mt-2 text-amber-700">在此之前，可先使用下方「电脑网页版（安装到桌面）」，体验基本一致。</p>
          </div>
        )}
      </div>

      {/* 电脑网页版 PWA */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-3xl">🌐</span>
          <div>
            <h2 className="font-bold text-slate-800">电脑网页版 · 安装到桌面</h2>
            <p className="text-xs text-slate-500 mt-0.5">无需下载安装包，浏览器一键加入桌面</p>
          </div>
        </div>
        <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
          <li>用 <b>Chrome</b> 或 <b>Edge</b> 浏览器打开 ERP 网址并登录</li>
          <li>点地址栏右侧的 <b>「安装」图标</b>（⊕ 或屏幕带箭头的小图标）</li>
          <li>桌面会出现「财务ERP」图标，双击打开即是独立窗口，与桌面版体验一致</li>
        </ol>
        <button
          onClick={() => openAddToDesktop()}
          className="mt-3 w-full text-center border border-brand-600 text-brand-600 font-semibold rounded-xl py-2.5 hover:bg-blue-50 transition"
        >
          📲 查看本机安装步骤
        </button>
      </div>
    </>
  );

  // ===== 手机版（旧区块，仅在电脑上访问时作参考展示） =====
  const LegacyMobileCard = (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-3xl">📱</span>
        <div>
          <h2 className="font-bold text-slate-800">手机版</h2>
          <p className="text-xs text-slate-500 mt-0.5">员工办公 / 客户报货均可在手机上使用</p>
        </div>
      </div>
      <div className="text-sm text-slate-600 space-y-2">
        <p>🟢 <b>方式一（推荐）</b>：微信搜索「荣牧科技 ERP」小程序，或扫码进入，点右上角「···」→「添加到桌面」。</p>
        <p>🟢 <b>方式二</b>：手机浏览器打开 ERP 网址 → 菜单 →「添加到主屏幕」，即可像 App 一样全屏使用网页版。</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-full bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* 标题 */}
        <div className="text-center">
          <div className="text-4xl mb-2">📊</div>
          <h1 className="text-2xl font-bold text-slate-800">财务ERP · 客户端下载</h1>
          <p className="text-sm text-slate-500 mt-1">网页版、桌面版、手机版共用同一账号与数据</p>
        </div>

        {/* 手机优先：手机端把"手机安装"放最上面；电脑端把 Windows 桌面版放最上面 */}
        {isMobile ? (
          <>
            {MobileCard}
            {DesktopCard}
          </>
        ) : (
          <>
            {DesktopCard}
            {LegacyMobileCard}
          </>
        )}

        {/* 返回 */}
        <div className="text-center pb-4">
          <a href="/login" className="text-sm text-brand-600 underline">← 返回登录</a>
        </div>
      </div>
    </div>
  );
}
